/**
 * English strings for the setup-guide DEEP_RESEARCH and KNOWLEDGE steps
 * (PREC-3700). Owned by the review-steps package — other tour strings live
 * in enOnboardingTour.ts.
 */
export const enOnboardingTourReview = {
	deepResearch: {
		saveContinue: "Save & continue",
		saveContinueInvited: "Looks good, continue",
		stages: {
			overview: "Overview",
			refine: "Refine",
			prompt: "Prompt",
		},
		overview: {
			title: "This is what Deep Research looks like for a lead",
			cards: {
				meetings: {
					title: "Prepares you for meetings",
					desc: "A full brief per lead — context, signals and talking points, ready before you reach out.",
				},
				messages: {
					title: "Powers your AI messages",
					desc: "The factual backbone your AI copy draws on, so every message cites real signals.",
				},
				prompt: {
					title: "Runs on your own prompt",
					desc: "Define it once and it applies to every contact — and you can create multiple prompts.",
				},
			},
			refineCta: "Refine the research",
		},
		refine: {
			title: "Run it on a real lead, then tell us what was off",
			desc: "Once you've seen a result above, pick what didn't fit and add a note. We'll suggest prompt changes you can accept or reject.",
			whyLabel: "What was off?",
			why: {
				shallow: "Not specific enough",
				missing: "Missed a signal",
				irrelevant: "Irrelevant section",
				format: "Wrong format",
				long: "Too long",
			},
			noteLabel: "Anything else? (optional)",
			notePlaceholder: "e.g. the talking points ignored their recent funding round",
			reviewPromptCta: "Review prompt",
			skipRefineCta: "Skip — the prompt looks good",
			analyzing: "Analyzing your feedback…",
			editManuallyHint: "Prefer to edit it yourself?",
			editManually: "Edit prompt manually",
		},
		suggestions: {
			heading: "Suggested changes",
			badge: "Suggested change",
			reject: "Reject",
			accept: "Accept",
			accepted: "Accepted",
			reRun: "Re-run Deep Research",
			reRunning: "Re-running…",
		},
		pendingTitle: "Your Deep Research prompt is still being generated",
		pendingBody:
			"We're building your company's research template in the background. You can continue with the setup and come back here any time.",
		pendingHint: "This usually takes a few minutes.",
		saveFailed: "Saving your changes failed — please try again.",
		nameLabel: "Template name",
		languageLabel: "Output language",
		chipNavLabel: "Prompt sections",
		chipStrategicFit: "Strategic Fit",
		untitledSection: "Section {{number}}",
		reorderHint: "Click a chip to jump to its section — drag chips to reorder the sections.",
		lockedOverlayLabel: "Editing is locked — click to request changes",
		fit: {
			title: "Strategic fit criteria",
			desc: "Define what makes a lead a strong, medium, or weak fit for your outreach.",
			strongLabel: "Strong fit",
			mediumLabel: "Medium fit",
			weakLabel: "Weak fit",
		},
		sections: {
			badge: "Section {{number}}",
			headerLabel: "Header",
			instructionLabel: "Instruction",
			add: "Add section",
			delete: "Delete section",
		},
		try: {
			title: "Try it on a real lead",
			desc: "Pick one of your contacts and watch Deep Research work with your template.",
			searchPlaceholder: "Search your contacts…",
			searching: "Searching…",
			noResults: "No contacts found.",
			empty: "Run Deep Research on a lead to see the result here.",
			running: "Researching…",
			view: "View result",
			unknownLead: "Unknown lead",
			fit: {
				STRONG: "Strong fit",
				MEDIUM: "Medium fit",
				WEAK: "Weak fit",
				NO_FIT: "No fit",
			},
		},
		executedRail: {
			title: "Executed leads",
		},
		viewer: {
			fallbackTitle: "Deep Research result",
			subtitle: "Deep Research result",
			close: "Close",
		},
	},
	knowledge: {
		continueReview: "Review Knowledge Base",
		continueOpen: "Open Knowledge Base",
		finishReviewed: "Looks good, finish setup",
		finishEdited: "I've made some changes, let's finish the setup",
		intro:
			"Your Knowledge Base is what Signalist knows about your company — it powers every AI draft. Here's what we've already prepared for you.",
		badgeAi: "Generated by AI",
		badgeWebsite: "Based on your website",
		boxes: {
			painPoints: {
				title: "Pain Points & Value Propositions",
				items: {
					0: "Pain points per target segment",
					1: "Value propositions that answer them",
					2: "Tailored to each persona",
				},
			},
			products: {
				title: "Products & Services",
				items: {
					0: "What you offer, in your own words",
					1: "Key features and benefits",
				},
			},
			caseStudies: {
				title: "Case Studies",
				items: {
					0: "Customer stories from your website",
					1: "Results and quotes to reference",
				},
			},
			objections: {
				title: "Objection Handling",
				items: {
					0: "Common objections in your market",
					1: "Suggested strategies and replies",
				},
			},
		},
		allSetTitle: "You're all set",
		allSetBodyEdited:
			"Your changes to the Knowledge Base are saved — every future draft will use them.",
		allSetBodyReviewed:
			"Your Knowledge Base is reviewed and ready — every future draft will build on it.",
		reviewAgain: "Review Knowledge Base again",
		pendingTitle: "Your Knowledge Base is still being generated",
		pendingBody:
			"We're reading your website and building your Knowledge Base in the background. You can finish the setup now — we'll notify you when it's ready.",
		returnBar: {
			title: "Reviewing your Knowledge Base",
			text: "You're reviewing your Knowledge Base as part of your setup. When it looks right, jump back into the guide.",
			cta: "Looks good — back to setup",
		},
		handoff: {
			title: "Your Knowledge Base is ready",
			body: "It's built from your answers and your website — the source every AI draft draws on. Take a look on the Knowledge Base page; we'll bring you right back to the setup when you're done.",
			open: "Review Knowledge Base",
		},
	},
	copywriting: {
		stages: {
			overview: "How it works",
			refine: "Refine",
			suggestions: "Suggestions",
		},
		overview: {
			title: "Every message is composed from four inputs",
			desc: "Signalist blends these into each draft. You'll refine a few real drafts next, and we'll turn your edits into lasting instructions.",
			refineCta: "Refine messages",
			inputs: {
				leadData: {
					title: "Enriched lead data",
					desc: "Who they are, their role, company and recent signals.",
				},
				deepResearch: {
					title: "Deep Research",
					desc: "The per-lead brief and personalization angle.",
				},
				knowledgeBase: {
					title: "Your Knowledge Base",
					desc: "What your company offers and the value you bring.",
				},
				toneOfVoice: {
					title: "Your tone of voice",
					desc: "Your personal voice, learned from your edits.",
				},
			},
		},
		refine: {
			title: "See the AI drafts and make them yours",
			desc: "Edit any message so it sounds like you. We learn from every change.",
			leadsLabel: "See the AI drafts for:",
			running: "Running",
			done: "Done",
			stepLabel: "Message {{step}}",
			linkedin: "LinkedIn message",
			emailSubject: "Email subject",
			emailBody: "Email body",
			nextCta: "Next",
			analyzeCta: "Analyze my changes",
		},
		suggestions: {
			title: "What to change — across your four inputs",
			desc: "We compared your edits to the drafts. Accept the changes you want applied to every future message.",
			analyzing: "Analyzing your changes…",
			accept: "Accept",
			reject: "Reject",
			applyCta: "Apply & regenerate",
			emptyTitle: "You're all set",
			emptyDesc:
				"We didn't spot any changes worth turning into a rule. Your drafts already look great.",
		},
	},
};
